// QA/preview shortcut: `?debug=<key>` force-assigns an experiment arm, seeds a
// qualifying survey profile into localStorage, and jumps to the screen that
// experiment changes. Optional `&arm=<arm>` overrides the forced arm (defaults
// to "variation"; pass "control" to preview the control side).
//
// Examples:
//   /?debug=web35&arm=variation          -> pricing page, no-trial urgency variant
//   /?debug=web34_checkout_cta&arm=variation -> annual checkout, "Try for $0.00" CTA
//   /?debug=welcome-kit&arm=variation&offer=reset -> welcome-kit trial pricing (web40)
//   /?debug=welcome-kit-checkout&arm=variation&checkout_test_mode=1 -> annual trial checkout with the kit
//   /?debug=<any_experiment_name>        -> just force that experiment, no seeding
//
// To force an experiment WITHOUT seeding/jumping (walk the quiz normally with it
// on), use `?force=<experiment>` instead (optional `&arm=`):
//   /?force=web35&arm=control
//   /?force=web30,web35:variation   ("name:arm" pins that
//     experiment's arm; names without one follow the shared &arm=)
//
// Forcing relies on getAssignedExperimentArm honoring the forceExperiment /
// forceArm localStorage keys (same mechanism used in production), so no Statsig
// assignment is needed locally. Safe to ship: it only acts when ?debug is passed
// and only writes to the current browser's localStorage.

import { LOCAL_STORAGE_KEY } from "./App";
import { OtherPrograms } from "./quiz/otherPrograms";
import { ScienceFamiliarity } from "./quiz/scienceFamiliarity";
import { SelfDescription } from "./quiz/describeSelf";
import { WhyConsistency } from "./quiz/whyConsistency";
import { Experience } from "./quiz/experience";
import { WEB29_OFFER_STORAGE_KEY } from "./web29Offer";

const WEB46_EXPERIMENT_NAME = "web46_match_offer";
const WEB46_MATCH_STORAGE_KEY = "bws_web46_match_offer";

const WEB46_EARLY_PROFILE: Record<string, unknown> = {
  goals: "Lose Weight",
  gender: "Male",
};

// A fully completed quiz so `/results` rehydrates and the chart renders.
const FULL_DEMO_PROFILE: Record<string, unknown> = {
  goals: "Lose Weight",
  gender: "Male",
  age: "30-39",
  heightUnit: "ft",
  heightRaw: 5,
  heightInches: 10,
  weight: 180,
  weightUnit: "lbs",
  goalWeight: 160,
  name: "Preview",
  bodytype: "Skinny",
  bodyFat: 35,
  describeSelf: "DietAndTraining",
  areaBody: ["Belly", "Love Handles", "Chest"],
  muscleFocus: "Chest",
  experience: "Beginner",
  email: "preview@example.com",
  duration: "0-6 months",
  motivation: "Confidence",
  shame: "Often",
  dissatisfaction: 3,
  consistency: 3,
  diet: 3,
  otherPrograms: ["OnlineFitnessProgram"],
  workouttrackingapp: "Yes",
  nutritiontrackingapp: "Yes",
  science: "Very",
  activity: "sedentary",
  cardio: "Rarely",
  comfort: "Never",
  availableDays: ["Monday", "Tuesday", "Wednesday"],
  numberWorkouts: "3",
  timeAvailable: "60",
  togetherConsistent: "false",
  focus: "Nutrition",
};

// The six personas from the PRD artifact's "Try it" panel, on our stored values.
const WEB46_PERSONAS: Record<string, Record<string, unknown>> = {
  sarah: {
    name: "Sarah",
    followingus: "Less than 6 months",
    otherPrograms: [OtherPrograms.CalorieCounting, OtherPrograms.IntermittentFasting],
    science: ScienceFamiliarity.Attempted,
    duration: "1-3 years",
    describeSelf: SelfDescription.StruggleConsistency,
    whyConsistency: WhyConsistency.Time,
    experience: Experience.Beginner,
  },
  mike: {
    name: "Mike",
    followingus: "2+ years",
    otherPrograms: [OtherPrograms.SelfGuidedFitnessProgram],
    science: ScienceFamiliarity.Very,
    duration: "3+ years",
    describeSelf: SelfDescription.SlowProgress,
    whyConsistency: WhyConsistency.Time,
    experience: Experience.Intermediate,
  },
  alex: {
    name: "Alex",
    followingus: "Just discovered us",
    otherPrograms: [OtherPrograms.PersonalTrainer, OtherPrograms.PaidMealPlans],
    science: ScienceFamiliarity.Somewhat,
    duration: "1-3 years",
    describeSelf: SelfDescription.StruggleConsistency,
    whyConsistency: WhyConsistency.Motivation,
    experience: Experience.Intermediate,
  },
  dana: {
    name: "Dana",
    followingus: "Never",
    otherPrograms: [],
    science: ScienceFamiliarity.Very,
    duration: "3+ years",
    describeSelf: SelfDescription.SlowProgress,
    whyConsistency: WhyConsistency.Time,
    experience: Experience.Advanced,
  },
  priya: {
    name: "Priya",
    followingus: "Just discovered us",
    otherPrograms: [],
    science: ScienceFamiliarity.Attempted,
    duration: "0-6 months",
    describeSelf: SelfDescription.DietAndTrainingNeedWork,
    whyConsistency: WhyConsistency.Time,
    experience: Experience.Beginner,
  },
  jo: {
    name: "Jo",
    followingus: "6 months to 1 year",
    otherPrograms: [],
    science: ScienceFamiliarity.Very,
    duration: "6-12 months",
    describeSelf: SelfDescription.StruggleConsistency,
    whyConsistency: WhyConsistency.ProgramHopping,
    experience: Experience.Intermediate,
  },
};

// FULL_DEMO_PROFILE minus the final "focus" answer: lands on the last quiz
// question so answering it completes the quiz naturally.
const { focus: _focus, ...LAST_QUESTION_PROFILE } = FULL_DEMO_PROFILE;

// web43: everything answered up to the gym-buddy entry page, choice not made.
const { togetherConsistent: _tc, ...GYM_BUDDY_ENTRY_PROFILE } = FULL_DEMO_PROFILE;

// Everything needed through the email step, so continuing from email opens the
// opening-arc projection screen immediately without seeding later-section answers.
const {
  duration: _arcDuration,
  motivation: _arcMotivation,
  shame: _arcShame,
  dissatisfaction: _arcDissatisfaction,
  consistency: _arcConsistency,
  diet: _arcDiet,
  otherPrograms: _arcOtherPrograms,
  workouttrackingapp: _arcWorkoutTracking,
  nutritiontrackingapp: _arcNutritionTracking,
  science: _arcScience,
  activity: _arcActivity,
  cardio: _arcCardio,
  comfort: _arcComfort,
  availableDays: _arcAvailableDays,
  numberWorkouts: _arcNumberWorkouts,
  timeAvailable: _arcTimeAvailable,
  togetherConsistent: _arcTogetherConsistent,
  focus: _arcFocus,
  ...ARC_CHART_PROFILE
} = FULL_DEMO_PROFILE;

type DebugScenario = {
  /** The Statsig experiment key to force. */
  experiment: string;
  /** Short aliases accepted in place of the full experiment key. */
  aliases?: string[];
  /** Survey answers to seed so the screen's trigger is satisfied. */
  data: Record<string, unknown>;
  /** Quiz page to land on (resolved by name against the live model). */
  currentPageName: string;
  /** If set, redirect here (e.g. "/results") instead of staying in the quiz. */
  redirectTo?: string;
  /** Keep the landing loading screen (default is to skip it). Set for
   * experiments that change the loading screen itself. */
  keepLandingLoading?: boolean;
  /** Extra experiments to force alongside the main one. Entries may pin their
   * own arm with "name:arm"; entries without an arm follow the shared ?arm=. */
  alsoForce?: string[];
  /** Arm to use when the debug URL omits `arm`. */
  defaultArm?: string;
};

const SCENARIOS: DebugScenario[] = [
  {
    experiment: "web39_paid_opening_arc",
    aliases: ["web39", "arc", "paidarc"],
    data: {},
    currentPageName: "transformationExpectation",
  },
  {
    experiment: "web39_paid_opening_arc",
    aliases: ["web39chart", "arcchart"],
    data: ARC_CHART_PROFILE,
    currentPageName: "email",
  },
  {
    // Lands directly on the food-scale question (inserted after proteinSplash
    // in the variation arm). FULL_DEMO_PROFILE has every earlier answer.
    experiment: "web32_scale_question",
    aliases: ["scale", "foodscale"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "foodScale",
  },
  {
    // Lands two questions before the HSA/FSA demand-test question
    // (hearaboutus → focus → hsaFsa) so the run-up is visible: in the
    // variation arm focus has lost its "Last question" lead-in and Continue
    // shows the HSA/FSA question; &arm=control goes from focus straight into
    // the loading screen.
    experiment: "web38_hsa_fsa_demand",
    aliases: ["web38", "hsa"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "hearaboutus",
  },
  {
    // Lands directly on the annual-plan checkout so the final submit button
    // ("Start trial" vs "Try for $0.00") is two clicks away: Continue with
    // Card → submit. Append &trial=false by hand to check the pay-today path.
    experiment: "web34_checkout_cta",
    aliases: ["web34", "cta"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/checkout?plan=committed",
  },
  {
    // web45: lands on the annual-plan checkout. &arm=variation shows "Have a
    // referral code?" on the payment heading; &arm=control is today's page.
    experiment: "web45_referral_code",
    aliases: ["web45", "referral-code", "refcode"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/checkout?plan=committed",
  },
  {
    // web35: no-trial urgency offer on the baseline pricing page. &arm=control
    // is the site baseline (timer + trial); &arm=variation the pay-today
    // variant. Add &offer=reset|expired|email for the offer states.
    experiment: "web35_no_trial_urgency",
    aliases: ["web35", "notrial", "pricing"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    // web41_start_date_reservation (organic-only demand test): pricing page
    // with the "Ready someday, not today?" links (&arm=variation).
    experiment: "web41_start_date_reservation",
    aliases: ["web41", "reserve", "reservation"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    // Jump straight to the reservation pitch page / coming-soon screen.
    experiment: "web41_start_date_reservation",
    aliases: ["web41-reserve", "reserve-page"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/reserve",
  },
  {
    experiment: "web41_start_date_reservation",
    aliases: ["web41-soon", "reserve-soon"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/reserve/soon",
  },
  {
    // web40_welcome_kit (paid-only test): pricing page with the welcome-kit
    // shelf + banner in the variation; &arm=control is the plain pricing page.
    experiment: "web40_welcome_kit",
    aliases: ["web40", "welcome-kit", "kit"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    // web40 intro (paid-only test): plays the loading bars, then the
    // welcome-kit intro screen; "Show me my plan" lands on the pricing page.
    experiment: "web40_welcome_kit",
    aliases: ["welcome-kit-intro", "web40-intro", "kit-intro"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
    keepLandingLoading: true,
  },
  {
    // Welcome kit at checkout (web40, paid-only test): annual trial checkout
    // with the kit summary. &arm=control is the plain checkout.
    experiment: "web40_welcome_kit",
    aliases: ["welcome-kit-checkout", "web40-checkout", "kit-checkout"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/checkout?plan=committed",
  },
  {
    // web40 organic twin: pricing page with the kit.
    experiment: "web40_welcome_kit_organic",
    aliases: ["welcome-kit-organic", "web40-organic", "kit-organic"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    // web40 organic: loading bars, then the welcome-kit intro, then pricing.
    experiment: "web40_welcome_kit_organic",
    aliases: ["welcome-kit-organic-intro", "web40-organic-intro", "kit-organic-intro"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
    keepLandingLoading: true,
  },
  {
    // web40 organic: annual trial checkout with the kit summary.
    experiment: "web40_welcome_kit_organic",
    aliases: ["welcome-kit-organic-checkout", "web40-organic-checkout", "kit-organic-checkout"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/checkout?plan=committed",
  },
  {
    // web40 v2 (shelf below the CTA, control banner + checkout): pricing page.
    experiment: "web40_welcome_kit_organic_v2",
    aliases: ["welcome-kit-v2", "web40-v2", "kit-v2"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    // web40 v2: loading bars, then the welcome-kit intro, then pricing.
    experiment: "web40_welcome_kit_organic_v2",
    aliases: ["welcome-kit-v2-intro", "web40-v2-intro", "kit-v2-intro"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
    keepLandingLoading: true,
  },
  {
    // web40 v2: annual trial checkout (control UI; offer_id still sent).
    experiment: "web40_welcome_kit_organic_v2",
    aliases: ["welcome-kit-v2-checkout", "web40-v2-checkout", "kit-v2-checkout"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/checkout?plan=committed",
  },
  {
    // web43: lands on the gym-buddy entry page. &arm=variation shows "Add a
    // second person to your plan?" with Yes / No thanks; control the current
    // "Train together" screen with Continue + Skip.
    experiment: "web43_gym_buddy_clarity",
    aliases: ["web43", "gym-buddy", "gymbuddy", "gym-buddy-clarity"],
    data: GYM_BUDDY_ENTRY_PROFILE,
    currentPageName: "togetherConsistent",
  },
  {
    // web43 pricing: opted in to the gym buddy, straight to the pricing page
    // (variation: two-person total + "Switch to 1-person plan").
    experiment: "web43_gym_buddy_clarity",
    aliases: ["web43-pricing", "gym-buddy-pricing", "gymbuddy-pricing"],
    data: { ...FULL_DEMO_PROFILE, togetherConsistent: "true" },
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    // web46 quiz preview: a short profile on an early question so the
    // personalization arm's persistent quiz banner is easy to inspect.
    experiment: WEB46_EXPERIMENT_NAME,
    aliases: ["web46"],
    data: WEB46_EARLY_PROFILE,
    currentPageName: "age",
    defaultArm: "personalization",
  },
  {
    // web46 pricing preview. `persona` replaces the reason-driving answers in
    // FULL_DEMO_PROFILE; Sarah is the default when no persona is supplied.
    experiment: WEB46_EXPERIMENT_NAME,
    aliases: ["web46-pricing"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
    defaultArm: "personalization",
  },
  {
    // web30_v3: only the checkout momentum line (no loading sequence). Lands
    // on the annual trial checkout; &arm=control shows the plain heading.
    experiment: "web30_v3_checkout_momentum",
    aliases: ["web30", "web30-v3", "momentum", "checkout-momentum"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/checkout?plan=committed",
  },
  {
    // web30_v3 from the last quiz question (male, 30-39 profile seeded):
    // answer it, sit through the plain loading, pick a plan, reach checkout.
    experiment: "web30_v3_checkout_momentum",
    aliases: ["web30-lastq", "lastq"],
    data: LAST_QUESTION_PROFILE,
    currentPageName: "focus",
    keepLandingLoading: true,
  },
];

export function maybeActivateDebugExperiment(): void {
  try {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const requestedArm = params.get("arm");
    let arm = requestedArm || "variation";

    // `?force=<experiment>` just flips the experiment on (no seeding, no jump) so
    // you can walk the quiz normally with it active. Accepts a comma-separated
    // list to force several at once, e.g. ?force=web24_pricing_extras,web35.
    const forceKey = params.get("force");
    if (forceKey) {
      const forced = forceKey
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean)
        .map((entry) => {
          // Support "name:arm" pinning — resolve only the name part through
          // the scenario aliases, keep the pinned arm attached.
          const [k, pinnedArm] = entry.split(":").map((part) => part.trim());
          const resolved =
            SCENARIOS.find((s) => s.experiment === k || s.aliases?.includes(k))
              ?.experiment ?? k;
          return pinnedArm ? `${resolved}:${pinnedArm}` : resolved;
        });
      localStorage.setItem("forceExperiment", forced.join(","));
      localStorage.setItem("forceArm", arm);
      // A from-the-top walk: drop whatever an earlier ?debug link left behind
      // in this browser (seeded quiz answers, skipped landing loading), so
      // the quiz starts at question 1 and the loading bars play.
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      localStorage.removeItem("disable_landing_loading");
      return;
    }

    const key = params.get("debug");
    if (!key) return;
    const scenario = SCENARIOS.find(
      (s) => s.experiment === key || s.aliases?.includes(key)
    );

    if (!scenario) {
      // Unknown key: treat it as an experiment name and just force the arm.
      // The app picks it up via getAssignedExperimentArm on next render.
      localStorage.setItem("forceExperiment", key);
      localStorage.setItem("forceArm", arm);
      return;
    }

    arm = requestedArm || scenario.defaultArm || "variation";

    let scenarioData = scenario.data;
    if (scenario.aliases?.includes("web46-pricing")) {
      const persona = (params.get("persona") || "sarah").toLowerCase();
      scenarioData = {
        ...FULL_DEMO_PROFILE,
        ...(WEB46_PERSONAS[persona] ?? WEB46_PERSONAS.sarah),
      };

      // Pricing previews always begin with a fresh live offer. The boot-time
      // location sync runs after this and can still apply `offer=expired` or
      // `offer=email` for those explicit QA states.
      localStorage.removeItem(WEB29_OFFER_STORAGE_KEY);
      if (params.get("reset_match") === "1") {
        localStorage.removeItem(WEB46_MATCH_STORAGE_KEY);
        params.delete("reset_match");
        const search = params.toString();
        window.history.replaceState(
          window.history.state,
          "",
          `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
        );
      }
    }

    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        // currentPageIndex must be truthy for the restore path to fire; actual
        // navigation resolves by name against the live model.
        currentPageIndex: 1,
        currentPageName: scenario.currentPageName,
        data: scenarioData,
      })
    );
    // skip the landing loading animation (unless the scenario tests it)
    if (scenario.keepLandingLoading) {
      localStorage.removeItem("disable_landing_loading");
    } else {
      localStorage.setItem("disable_landing_loading", "true");
    }
    // force the experiment arm on (no Statsig assignment locally)
    localStorage.setItem(
      "forceExperiment",
      [scenario.experiment, ...(scenario.alsoForce ?? [])].join(",")
    );
    localStorage.setItem("forceArm", arm);

    if (scenario.redirectTo) {
      const target = new URL(scenario.redirectTo, window.location.origin);
      if (window.location.pathname !== target.pathname) {
        // Keep explicit QA settings (test mode, offer state, plan, trial) and
        // attribution when jumping from a root debug link into the real app.
        params.forEach((value, name) => target.searchParams.set(name, value));
        target.searchParams.set("debug", key);
        target.searchParams.set("arm", arm);
        target.hash = window.location.hash;
        window.location.replace(`${target.pathname}${target.search}${target.hash}`);
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[debugExperiment] failed to activate", e);
  }
}
